/**
 * P&L Reports Service Layer — pivot-on-read architecture.
 *
 * All per-report endpoints use pnl_report_id as the path parameter.
 *
 * Endpoint map:
 *   GET  /pnl-reports/config              → fetchPnlReportConfig
 *   GET  /pnl-reports                     → fetchPnlReportList
 *   POST /pnl-reports                     → createPnlReport
 *   GET  /pnl-reports/{pnl_report_id}/config   → fetchPnlReportMetadata
 *   POST /pnl-reports/{pnl_report_id}/query    → queryPnlReport
 *   PUT  /pnl-reports/{pnl_report_id}          → editPnlReport   (full config, regen)
 *   PATCH /pnl-reports/{pnl_report_id}         → patchPnlReport  (name/description only)
 *   POST /pnl-reports/{pnl_report_id}/refresh  → refreshPnlReport
 *   DELETE /pnl-reports/{pnl_report_id}        → deletePnlReport
 */
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { STAGES } from 'src/constants/generic-constants';
import {
  PnlCreateReportRequest,
  PnlCreateReportResponse,
  PnlCreateSnapshotRequest,
  PnlCreateSnapshotResponse,
  PnlDeleteReportResponse,
  PnlDeleteSnapshotResponse,
  PnlEditReportRequest,
  PnlEditReportResponse,
  PnlPatchReportRequest,
  PnlPatchReportResponse,
  PnlQueryRequest,
  PnlQueryResponse,
  PnlRefreshReportResponse,
  PnlReportConfig,
  PnlReportConfigResponse,
  PnlReportListResponse,
  PnlSnapshotListResponse
} from '../interfaces/pnl-report.interfaces';

// ─── Configuration ───────────────────────────────────────────────────────────

const XPT_API_DOMAIN_BY_STAGE: Record<string, string> = {
  [STAGES.DEV]: 'alpha.api.xpt.device.finance.amazon.dev',
  [STAGES.ALPHA]: 'alpha.api.xpt.device.finance.amazon.dev',
  [STAGES.BETA]: 'beta.api.xpt.device.finance.amazon.dev',
  [STAGES.PROD]: 'api.xpt.device.finance.amazon.dev'
};

const getXptApiBaseUrl = (): string => {
  const stage = ENVIRONMENT_VARIABLES.env.Stage;
  const domain = XPT_API_DOMAIN_BY_STAGE[stage] ?? XPT_API_DOMAIN_BY_STAGE[STAGES.ALPHA];
  return `https://${domain}`;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Cache the aws-amplify/auth module import at module scope (bundle-conditional):
// dynamic-imported once, reused thereafter — avoids a per-call import waterfall.
let authModulePromise: Promise<typeof import('aws-amplify/auth')> | null = null;
const loadAuthModule = (): Promise<typeof import('aws-amplify/auth')> =>
  (authModulePromise ??= import('aws-amplify/auth'));

// Short-TTL token cache + in-flight dedupe (client-swr-dedup, async-parallel):
// - Cache the ID token for a minute so bursts of API calls (config + list +
//   query) share one auth round-trip instead of N.
// - If a fetch is already in flight, concurrent callers await the same promise.
// Amplify handles real token refresh; this only dedupes our access to it.
const TOKEN_TTL_MS = 60_000;
let tokenCache: { token: string; expiresAt: number } | null = null;
let tokenInFlight: Promise<string> | null = null;

const getIdToken = (): Promise<string> => {
  const now = Date.now();
  if (tokenCache && tokenCache.expiresAt > now) return Promise.resolve(tokenCache.token);
  if (tokenInFlight) return tokenInFlight;
  tokenInFlight = (async () => {
    try {
      const { fetchAuthSession } = await loadAuthModule();
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('No ID token found in the current session');
      tokenCache = { token, expiresAt: Date.now() + TOKEN_TTL_MS };
      return token;
    } finally {
      tokenInFlight = null;
    }
  })();
  return tokenInFlight;
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Single request helper — collapses the previous 5 near-duplicate apiVerb
 * functions. Body is JSON-stringified only when present.
 */
const apiRequest = async <T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> => {
  const token = await getIdToken();
  const response = await fetch(`${getXptApiBaseUrl()}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });
  if (!response.ok) {
    throw new Error(`API error ${response.status}: ${await response.text()}`);
  }
  return (await response.json()) as T;
};

const apiGet = <T>(path: string, signal?: AbortSignal) =>
  apiRequest<T>('GET', path, undefined, signal);
const apiPost = <T>(path: string, body: unknown, signal?: AbortSignal) =>
  apiRequest<T>('POST', path, body, signal);
const apiPut = <T>(path: string, body: unknown, signal?: AbortSignal) =>
  apiRequest<T>('PUT', path, body, signal);
const apiPatch = <T>(path: string, body: unknown, signal?: AbortSignal) =>
  apiRequest<T>('PATCH', path, body, signal);
const apiDelete = <T>(path: string, signal?: AbortSignal) =>
  apiRequest<T>('DELETE', path, undefined, signal);

// ─── Global Config ───────────────────────────────────────────────────────────

/** GET /pnl-reports/config — available scenarios / dimensions. */
export const fetchPnlReportConfig = async (signal?: AbortSignal): Promise<PnlReportConfig> => {
  const raw = await apiGet<PnlReportConfig>('/pnl-reports/config', signal);
  return {
    ...raw,
    available_scenarios: raw.available_scenarios.map((s) => ({
      ...s,
      reporting_scenario_id: Number(s.reporting_scenario_id),
      months: s.months.map(Number)
    }))
  };
};

// ─── Report List ─────────────────────────────────────────────────────────────

/** GET /pnl-reports — list of all reports with lightweight metadata. */
export const fetchPnlReportList = async (signal?: AbortSignal): Promise<PnlReportListResponse> => {
  return apiGet<PnlReportListResponse>('/pnl-reports', signal);
};

// ─── Per-Report Metadata (View Config page + Viewer mount + status polling) ──

/**
 * GET /pnl-reports/{pnl_report_id}/config — per-report metadata only. NO row data.
 * Cheap and fast; called by:
 *  - View Config page (renders read-only form from metadata)
 *  - List polling for reports in 'generating' state
 *  - Report Viewer on mount (parallel to first /query call)
 */
export const fetchPnlReportMetadata = async (
  pnlReportId: number,
  signal?: AbortSignal
): Promise<PnlReportConfigResponse> => {
  return apiGet<PnlReportConfigResponse>(
    `/pnl-reports/${pnlReportId}/config`,
    signal
  );
};

// ─── Query (pivot-on-read data endpoint) ─────────────────────────────────────

/**
 * POST /pnl-reports/{pnl_report_id}/query — pivot-on-read data.
 *
 * Called by:
 *  - Report Viewer on mount (default group_by, empty filters)
 *  - Every drill-down (extend group_by, lock parent-row values in filters)
 *  - Every filter-bar change (update filters block)
 *
 * Response includes column_groups, calculated_columns, rows — plus a light metadata block
 * (no dimension_values; caller already has that from /config).
 */
export const queryPnlReport = async (
  pnlReportId: number,
  request: PnlQueryRequest,
  signal?: AbortSignal
): Promise<PnlQueryResponse> => {
  return apiPost<PnlQueryResponse>(
    `/pnl-reports/${pnlReportId}/query`,
    request,
    signal
  );
};

// ─── Create ──────────────────────────────────────────────────────────────────

/** POST /pnl-reports — create a new report (async — Fargate does the work). */
export const createPnlReport = async (
  request: PnlCreateReportRequest,
  signal?: AbortSignal
): Promise<PnlCreateReportResponse> => {
  return apiPost<PnlCreateReportResponse>('/pnl-reports', request, signal);
};

// ─── Edit (full config, regenerate in place) ─────────────────────────────────

/**
 * PUT /pnl-reports/{pnl_report_id} — replace the report's full config and
 * regenerate its cube under the same id (async — Fargate does the work).
 * Snapshots taken from this report are unaffected.
 */
export const editPnlReport = async (
  pnlReportId: number,
  request: PnlEditReportRequest,
  signal?: AbortSignal
): Promise<PnlEditReportResponse> => {
  return apiPut<PnlEditReportResponse>(`/pnl-reports/${pnlReportId}`, request, signal);
};

// ─── Patch (name / description only, no regeneration) ────────────────────────

/**
 * PATCH /pnl-reports/{pnl_report_id} — update name and/or description without
 * rebuilding the cube. Synchronous; returns the updated fields immediately.
 */
export const patchPnlReport = async (
  pnlReportId: number,
  request: PnlPatchReportRequest,
  signal?: AbortSignal
): Promise<PnlPatchReportResponse> => {
  return apiPatch<PnlPatchReportResponse>(`/pnl-reports/${pnlReportId}`, request, signal);
};

// ─── Refresh ─────────────────────────────────────────────────────────────────

/**
 * POST /pnl-reports/{pnl_report_id}/refresh — re-run the saved query against current data.
 */
export const refreshPnlReport = async (
  pnlReportId: number,
  signal?: AbortSignal
): Promise<PnlRefreshReportResponse> => {
  return apiPost<PnlRefreshReportResponse>(
    `/pnl-reports/${pnlReportId}/refresh`,
    {},
    signal
  );
};

// ─── Delete ──────────────────────────────────────────────────────────────────

/** DELETE /pnl-reports/{pnl_report_id} — deletes metadata + raw cube + views. */
export const deletePnlReport = async (
  pnlReportId: number,
  signal?: AbortSignal
): Promise<PnlDeleteReportResponse> => {
  return apiDelete<PnlDeleteReportResponse>(
    `/pnl-reports/${pnlReportId}`,
    signal
  );
};

// ─── Snapshots ───────────────────────────────────────────────────────────────

/** POST /pnl-reports/{pnl_report_id}/snapshots — create a snapshot from a ready report. */
export const createPnlSnapshot = async (
  pnlReportId: number,
  request: PnlCreateSnapshotRequest,
  signal?: AbortSignal
): Promise<PnlCreateSnapshotResponse> => {
  return apiPost<PnlCreateSnapshotResponse>(
    `/pnl-reports/${pnlReportId}/snapshots`,
    request,
    signal
  );
};

/** GET /pnl-reports/{pnl_report_id}/snapshots — list snapshots for a specific report. */
export const fetchPnlReportSnapshots = async (
  pnlReportId: number,
  signal?: AbortSignal
): Promise<PnlSnapshotListResponse> => {
  return apiGet<PnlSnapshotListResponse>(`/pnl-reports/${pnlReportId}/snapshots`, signal);
};

/** GET /pnl-snapshots — list all active snapshots across all reports. */
export const fetchPnlSnapshotList = async (signal?: AbortSignal): Promise<PnlSnapshotListResponse> => {
  return apiGet<PnlSnapshotListResponse>('/pnl-snapshots', signal);
};

/** GET /pnl-snapshots/{pnl_snapshot_id}/config — snapshot metadata (same shape as report config). */
export const fetchPnlSnapshotConfig = async (
  pnlSnapshotId: number,
  signal?: AbortSignal
): Promise<PnlReportConfigResponse> => {
  return apiGet<PnlReportConfigResponse>(`/pnl-snapshots/${pnlSnapshotId}/config`, signal);
};

/** POST /pnl-snapshots/{pnl_snapshot_id}/query — pivot-on-read against frozen data. */
export const queryPnlSnapshot = async (
  pnlSnapshotId: number,
  request: PnlQueryRequest,
  signal?: AbortSignal
): Promise<PnlQueryResponse> => {
  return apiPost<PnlQueryResponse>(`/pnl-snapshots/${pnlSnapshotId}/query`, request, signal);
};

/** DELETE /pnl-snapshots/{pnl_snapshot_id} — soft-delete a snapshot. */
export const deletePnlSnapshot = async (
  pnlSnapshotId: number,
  signal?: AbortSignal
): Promise<PnlDeleteSnapshotResponse> => {
  return apiDelete<PnlDeleteSnapshotResponse>(`/pnl-snapshots/${pnlSnapshotId}`, signal);
};
